import { PasseriniAPI } from '@/services/apiService';
import { toArchivadoParam } from '@/hooks/useTramites/utils';
import {
  KANBAN_COLUMN_QUERIES,
  getDefaultColumnSort,
} from '@/components/TramitesKanban/kanbanUtils';
import type {
  EscrituraKanbanStatus,
  KanbanFilters,
  KanbanPage,
  KanbanSort,
  TramiteKanbanItem,
} from '@/components/TramitesKanban/types';

/**
 * 40 y no 20: la columna "Inscripta" tiene cientos de escrituras y con tandas
 * de 20 el usuario tenía que scrollear y esperar una vez por cada 20 tarjetas.
 * El backend acepta hasta 50 (TRAMITES_PER_PAGE_MAX).
 */
export const KANBAN_PAGE_SIZE = 40;

interface FetchKanbanPageParams {
  status: EscrituraKanbanStatus;
  page: number;
  filters: KanbanFilters;
  /** Orden elegido para la columna; sin esto se usa el de fábrica */
  sort?: KanbanSort;
  perPage?: number;
  signal?: AbortSignal;
}

const toPositiveInt = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 1 ? Math.trunc(parsed) : fallback;
};

const toCount = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : fallback;
};

/**
 * Normaliza la respuesta del paginador. Una fila sin `id` rompe las keys de
 * React y el reemplazo quirúrgico de la caché, así que se descarta acá en vez
 * de dejar que falle más adelante y lejos de la causa.
 */
const normalizePage = (payload: unknown, requestedPage: number): KanbanPage => {
  const body = (payload ?? {}) as Record<string, unknown>;
  const rawRows = Array.isArray(body.data) ? body.data : [];

  const data = rawRows.filter(
    (row): row is TramiteKanbanItem =>
      !!row && typeof row === 'object' && (row as TramiteKanbanItem).id != null,
  );

  const currentPage = toPositiveInt(body.current_page, requestedPage);
  // `last_page` nunca puede ser menor que la página que devolvió el servidor:
  // si lo fuera, `getNextPageParam` cortaría la paginación antes de tiempo.
  const lastPage = Math.max(toPositiveInt(body.last_page, currentPage), currentPage);

  return {
    data,
    current_page: currentPage,
    last_page: lastPage,
    total: toCount(body.total, data.length),
  };
};

/**
 * Trae una página de una columna del kanban.
 *
 * Cada columna es una query independiente sobre `/tramites`: el estado se
 * resuelve server-side con `escritura_filter` y el payload llega aligerado
 * con `view=kanban` (sin `user` ni `parametros`, y con el `presupuesto`
 * recortado a lo que leen los indicadores).
 */
export const fetchTramitesKanbanPage = async ({
  status,
  page,
  filters,
  sort,
  perPage = KANBAN_PAGE_SIZE,
  signal,
}: FetchKanbanPageParams): Promise<KanbanPage> => {
  const { sortField, sortDirection } = sort ?? getDefaultColumnSort(status);
  const requestedPage = toPositiveInt(page, 1);

  const params: Record<string, string | number> = {
    view: 'kanban',
    page: requestedPage,
    per_page: perPage,
    escritura_filter: KANBAN_COLUMN_QUERIES[status].escrituraFilter,
    sort: sortField,
    order: sortDirection,
    search: filters.search ?? '',
  };

  if (filters.dateFrom && filters.dateFrom !== 'Invalid Date' && filters.dateFrom.trim() !== '') {
    params.date_from = filters.dateFrom;
  }
  if (filters.dateTo && filters.dateTo !== 'Invalid Date' && filters.dateTo.trim() !== '') {
    params.date_to = filters.dateTo;
  }
  if (filters.dateField) {
    params.date_field = filters.dateField;
  }
  if (filters.registroFilter && filters.registroFilter !== 'todos') {
    params.numregistro = filters.registroFilter;
  }
  if (filters.estadoFilter && filters.estadoFilter !== 'todos') {
    params.estado = filters.estadoFilter;
  }
  if (filters.bloqueadaFilter && filters.bloqueadaFilter !== 'todos') {
    params.bloqueado = filters.bloqueadaFilter === 'si' ? '1' : '0';
  }
  if (filters.assignedUserId) {
    params.assigned_user_id = filters.assignedUserId;
  }

  // Siempre explícito, igual que en el listado de tabla: las archivadas quedan
  // fuera del tablero salvo que se pidan.
  params.archivado = toArchivadoParam(filters.archivadoFilter);

  const response = await PasseriniAPI.get('/tramites', { params, signal });

  return normalizePage(response.data, requestedPage);
};
